'use client';

import { createContext, ReactNode, useContext, useEffect, useState } from 'react';
import { useRouter } from 'next/router';
import isEqual from 'lodash/isEqual';
import isString from 'lodash/isString';

import { getContainer } from '@framework/di/getContainer';
import { AlertManager } from '@creditflowresearch/uilib/Alert';
import { useMessageManager } from '@framework/message';
import { IArticleWithMappedData } from '@/creditflowresearch/domain/vo/ArticlesAndStats/Article';
import { useBreadcrumbUpdates } from '@/creditflowresearch/ui/common/hooks';
import { fetchArticleById } from './helpers';
import { initialArticle } from './constants';
import getServerSideArticleBreadcrumbs
  from '@/creditflowresearch/application/Breadcrumbs/serverSide/getServerSideArticleBreadcrumbs';
import UserPlan from '@creditflowresearch/user-plan/UserPlan';
import { useUserPlanContext } from '@/creditflowresearch/application/User/UserPlan/UserPlanContext';
import { clearDownloadParams, UrlParamsToClear } from '@/creditflowresearch/application/ArticlesAndStats/helpers';
import useDownloadAttachment from '@/creditflowresearch/application/ArticlesAndStats/useDownloadAttachment';
import { checkAccess } from '@/creditflowresearch/application/User/access/checkAccess';
import { UserPlanFeature } from '@creditflowresearch/user-plan/vo/IUserPlanResponse';

const useArticle = (articleFromServer: IArticleWithMappedData) => {
  const [article, setArticle] = useState<IArticleWithMappedData>(articleFromServer);
  const [isInitialized, setIsInitialized] = useState(!articleFromServer.hasServerError);
  const { downloadAttachment } = useDownloadAttachment();
  const { plan, checkAccessByFeature } = useUserPlanContext();
  const router = useRouter();
  const { getErrorMessage } = useMessageManager();
  const { query } = router;

  const { articleId, uploadId, displayName } = query;
  const container = getContainer();
  const { updateBreadcrumbs } = useBreadcrumbUpdates();

  useEffect(() => {
    if (articleFromServer.hasServerError) {
      getArticleById();
      return;
    }
    if (!isEqual(article, articleFromServer)) {
      setArticle(articleFromServer);
    }
  }, [articleFromServer]);

  useEffect(() => {
    const hasAccess = checkAccess([
      UserPlan.isSatisfiedPremiumPlan(plan),
      checkAccessByFeature(UserPlanFeature.CommentaryAndDealFlow),
    ]);
    if (!hasAccess) {
      return;
    }

    if (!!uploadId && isString(uploadId) && !!displayName && isString(displayName)) {
      downloadAttachment({
        displayName,
        uploadId,
      });
      clearDownloadParams(router, UrlParamsToClear.articleId);
    }
  }, []);

  const getArticleById = async () => {
    try {
      const article = await fetchArticleById(Number(articleId), container);
      setArticle(article);
      updateBreadcrumbs(getServerSideArticleBreadcrumbs(router, article.headline));
    } catch (e) {
      AlertManager.error(getErrorMessage(e));
      setArticle(initialArticle);
    } finally {
      setIsInitialized(true);
    }
  };

  return {
    article,
    downloadAttachment,
    isInitialized,
  };
};

type ArticleContextType = ReturnType<typeof useArticle>;

export const ArticleContext = createContext<ArticleContextType>(null);

export const useArticleContext = () => {
  const context = useContext(ArticleContext);
  if (!context) {
    throw new Error('useArticleContext must be used within the ArticleContext');
  }
  return context;
};

interface IProps {
  children: ReactNode,
  article: IArticleWithMappedData,
}

const ArticleContextProvider = ({ children, article }: IProps) => (
  <ArticleContext.Provider value={ useArticle(article) }>
    { children }
  </ArticleContext.Provider>
);

export default ArticleContextProvider;
